import { Box, Flex, Grid, IconButton, Image, PseudoBox, Text, useColorMode } from '@chakra-ui/core';
import { useObserver } from 'mobx-react';
import React, { useEffect } from 'react';

import { useStores } from '../hooks/useStores';
import { AuthProviders, UserRoles } from '../types/shared';

function useStoreData() {
    const { store } = useStores();
    return useObserver(() => ({
        playlists: Array.from(store.me.playlists.values()),
        getPlaylists: store.me.getPlaylists,
        selectPlaylist: store.me.selectPlaylist,
        selectedPlaylist: store.me.playlists.get(store.me.selectedPlaylist),
        selectedPlaylistTracks: store.me.selectedPlaylistTracks,
        deselectPlaylist: store.me.deselectPlaylist,
        addToQueue: store.playback.addToQueue,
        search: store.search.search,
        results: store.search.results,
        cleanResults: store.search.cleanResults,
        isSearching: store.search.isSearching,
        userRole: store.auth.role,
        authMethod: store.auth.authMethod,
        searchTerm: store.search.searchTerm,
        selectAlbum: store.search.selectAlbum,
        selectedAlbumTracks: store.search.selectedAlbumTracks,
        selectedAlbum: store.search.selectedAlbum,
        deselectAlbum: store.search.deselectAlbum,
    }));
}

const BrowseMusicPanel = () => {
    const {
        playlists,
        getPlaylists,
        selectPlaylist,
        selectedPlaylist,
        selectedPlaylistTracks,
        deselectPlaylist,
        addToQueue,
        searchTerm,
        results,
        cleanResults,
        isSearching,
        userRole,
        authMethod,
        selectAlbum,
        selectedAlbumTracks,
        selectedAlbum,
        deselectAlbum,
    } = useStoreData();
    const { colorMode } = useColorMode();
    const color = { light: 'gray.900', dark: 'gray.100' };

    useEffect(() => {
        cleanResults();
        if (
            !playlists.length &&
            (userRole === UserRoles.HOST || (userRole === UserRoles.GUEST && authMethod === AuthProviders.SPOTIFY))
        ) {
            getPlaylists();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // TODO: Split this component (SearchResults, TrackResults, AlbumResults, UserPlaylists, etc..)
    return (
        <Flex overflow="auto" mr="-15px" flex={1} position="relative" direction="column" color={color[colorMode]}>
            {/* User Playlists */}
            <Flex direction="column">
                {searchTerm ? (
                    !isSearching ? (
                        selectedAlbum ? (
                            <Box>
                                <Flex alignItems="center" mb="1rem">
                                    <IconButton
                                        onClick={() => deselectAlbum()}
                                        mr=".5rem"
                                        fontSize="30px"
                                        size="lg"
                                        aria-label="Back"
                                        variant="ghost"
                                        icon="arrow-back"
                                    />
                                    <Image
                                        mr="1rem"
                                        size={['60px', '70px', '70px']}
                                        objectFit="cover"
                                        alt={selectedAlbum.name}
                                        src={selectedAlbum.image}
                                    />
                                    <Flex direction="column">
                                        <Text maxW={['200px', '300px', '100%']} isTruncated fontSize={['18px', '25px', '25px']}>
                                            {selectedAlbum.name}
                                        </Text>
                                        <Text
                                            maxW={['200px', '300px', '100%']}
                                            isTruncated
                                            opacity={0.8}
                                            fontSize={['16px', '20px', '20px']}
                                        >
                                            {selectedAlbum.artist}
                                        </Text>
                                    </Flex>
                                </Flex>
                                <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={1}>
                                    {selectedAlbumTracks.length ? (
                                        selectedAlbumTracks.map(track => {
                                            return (
                                                <PseudoBox
                                                    _hover={{ bg: 'rgba(0,0,0,.05)' }}
                                                    as="div"
                                                    d="flex"
                                                    p=".5rem 0"
                                                    minH={['60px', '80px', '80px']}
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    transition="all .3s ease"
                                                    key={`playlistTrack-${track.id}`}
                                                >
                                                    <IconButton
                                                        mr=".5rem"
                                                        variant="ghost"
                                                        aria-label="Add track to queue"
                                                        icon="add"
                                                        onClick={() => {
                                                            addToQueue(track);
                                                        }}
                                                    ></IconButton>
                                                    <Flex direction="column" flex={1}>
                                                        <Text
                                                            maxW={['250px', '350px', '100%']}
                                                            isTruncated
                                                            flex={1}
                                                            color={color[colorMode]}
                                                        >
                                                            {track.name}
                                                        </Text>
                                                        <Text
                                                            fontSize="14px"
                                                            opacity={0.7}
                                                            maxW={['250px', '350px', '100%']}
                                                            isTruncated
                                                            flex={1}
                                                            color={color[colorMode]}
                                                        >
                                                            {track.artists}
                                                        </Text>
                                                    </Flex>
                                                </PseudoBox>
                                            );
                                        })
                                    ) : (
                                        <Text>Loading... hey</Text>
                                    )}
                                </Grid>
                            </Box>
                        ) : (
                            <>
                                <Box>
                                    <Text fontSize={['20px', '25px', '25px']}>Albums</Text>
                                    <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
                                        {results &&
                                            results.albums.size &&
                                            Array.from(results.albums.values()).map((album, index) => {
                                                return (
                                                    <PseudoBox
                                                        _hover={{ cursor: 'pointer', bg: 'rgba(0,0,0,.05)' }}
                                                        as="div"
                                                        d="flex"
                                                        p=".5rem 0"
                                                        minH={['60px', '80px', '80px']}
                                                        alignItems="center"
                                                        transition="all .3s ease"
                                                        key={`searchResult-${album.id}`}
                                                        onClick={() => selectAlbum(album.id)}
                                                    >
                                                        <Image
                                                            size={['50px', '60px', '60px']}
                                                            objectFit="cover"
                                                            alt="Playlist"
                                                            src={album.image}
                                                        />
                                                        <Flex direction="column" pl="1rem" justifyContent="space-between">
                                                            <Text
                                                                maxW={['250px', '350px', '100%']}
                                                                isTruncated
                                                                color={color[colorMode]}
                                                            >
                                                                {album.name}
                                                            </Text>
                                                            <Text
                                                                maxW={['250px', '350px', '100%']}
                                                                isTruncated
                                                                opacity={0.8}
                                                                fontSize="15px"
                                                            >
                                                                {album.artist}
                                                            </Text>
                                                        </Flex>
                                                    </PseudoBox>
                                                );
                                            })}
                                    </Grid>
                                </Box>
                                <Box>
                                    <Text mt="1rem" fontSize={['20px', '25px', '25px']}>
                                        Tracks
                                    </Text>
                                    <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
                                        {results &&
                                            results.tracks.length &&
                                            results.tracks.map((track, index) => {
                                                return (
                                                    <PseudoBox
                                                        _hover={{ bg: 'rgba(0,0,0,.05)' }}
                                                        as="div"
                                                        d="flex"
                                                        p=".5rem 0"
                                                        minH={['60px', '80px', '80px']}
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        key={`searchResult-${track.id}`}
                                                    >
                                                        <IconButton
                                                            mr=".5rem"
                                                            variant="ghost"
                                                            aria-label="Add track to queue"
                                                            icon="add"
                                                            onClick={() => {
                                                                addToQueue(track);
                                                            }}
                                                        ></IconButton>
                                                        <Flex direction="column" flex={1}>
                                                            <Text maxW={['250px', '350px', '100%']} isTruncated>
                                                                {track.name}
                                                            </Text>
                                                            <Text
                                                                fontSize="14px"
                                                                opacity={0.7}
                                                                maxW={['250px', '350px', '100%']}
                                                                isTruncated
                                                            >
                                                                {track.artists}
                                                            </Text>
                                                        </Flex>
                                                    </PseudoBox>
                                                );
                                            })}
                                    </Grid>
                                </Box>
                            </>
                        )
                    ) : (
                        <Text>Loading...</Text>
                    )
                ) : (
                    ''
                )}
                {!selectedPlaylist && !searchTerm && playlists.length && (
                    <Box>
                        <Text fontSize={['20px', '25px', '25px']}>My Playlists</Text>
                        <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={1}>
                            {playlists.length &&
                                playlists.map(playlist => {
                                    return (
                                        <PseudoBox
                                            _hover={{ cursor: 'pointer', bg: 'rgba(0,0,0,.05)' }}
                                            as="div"
                                            d="flex"
                                            p=".5rem 0"
                                            minH={['60px', '80px', '80px']}
                                            alignItems="center"
                                            transition="all .3s ease"
                                            key={`playlist-${playlist.id}`}
                                            onClick={() => selectPlaylist(playlist.id)}
                                        >
                                            <Image
                                                size={['50px', '60px', '60px']}
                                                objectFit="cover"
                                                alt="Playlist"
                                                src={playlist.imageUrl}
                                            />
                                            <Text
                                                maxW={['250px', '350px', '100%']}
                                                isTruncated
                                                pl="1rem"
                                                color={color[colorMode]}
                                            >
                                                {playlist.name}
                                            </Text>
                                        </PseudoBox>
                                    );
                                })}
                        </Grid>
                    </Box>
                )}
                {selectedPlaylist && !searchTerm && (
                    <Box>
                        <Flex alignItems="center" mb="1rem">
                            <IconButton
                                onClick={() => deselectPlaylist()}
                                mr=".5rem"
                                fontSize="30px"
                                size="lg"
                                aria-label="Back"
                                variant="ghost"
                                icon="arrow-back"
                            />
                            <Image
                                mr="1rem"
                                size={['60px', '70px', '70px']}
                                objectFit="cover"
                                alt={selectedPlaylist.name}
                                src={selectedPlaylist.imageUrl}
                            />
                            <Text maxW={['200px', '300px', '100%']} isTruncated fontSize={['18px', '25px', '25px']}>
                                {selectedPlaylist.name}
                            </Text>
                        </Flex>
                        <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={1}>
                            {selectedPlaylistTracks.length ? (
                                selectedPlaylistTracks.map(track => {
                                    return (
                                        <PseudoBox
                                            _hover={{ bg: 'rgba(0,0,0,.05)' }}
                                            as="div"
                                            d="flex"
                                            p=".5rem 0"
                                            minH={['60px', '80px', '80px']}
                                            justifyContent="space-between"
                                            alignItems="center"
                                            transition="all .3s ease"
                                            key={`playlistTrack-${track.id}`}
                                        >
                                            <IconButton
                                                mr=".5rem"
                                                variant="ghost"
                                                aria-label="Add track to queue"
                                                icon="add"
                                                onClick={() => {
                                                    addToQueue(track);
                                                }}
                                            ></IconButton>
                                            <Flex direction="column" flex={1}>
                                                <Text
                                                    maxW={['250px', '350px', '100%']}
                                                    isTruncated
                                                    flex={1}
                                                    color={color[colorMode]}
                                                >
                                                    {track.name}
                                                </Text>
                                                <Text
                                                    fontSize="14px"
                                                    opacity={0.7}
                                                    flex={1}
                                                    color={color[colorMode]}
                                                    maxW={['250px', '350px', '100%']}
                                                    isTruncated
                                                >
                                                    {track.artists}
                                                </Text>
                                            </Flex>
                                        </PseudoBox>
                                    );
                                })
                            ) : (
                                <Text>Loading...</Text>
                            )}
                        </Grid>
                    </Box>
                )}
            </Flex>
        </Flex>
    );
};

export default BrowseMusicPanel;
